import { Button, Text, VSpacer } from '@/components/DesignSystem';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { DashboardModal } from '@/pages/CustomerDashboard/DashboardModal';
import { Color } from '@/themes/MUITheme/palette';
import { ApiPointsHistory } from '@api/interfaces/ApiPointsHistory';
import { Box, Divider, Stack, useTheme } from '@mui/material';
import { TransactionType } from '@shared/enums';
import { formatMediumDate, formatNumber, getAccountName } from '@shared/utilities';
import { TFunction } from 'i18next';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

function getManualAdjustmentTitle (type: TransactionType, t: TFunction): string {
  switch (type) {
    case TransactionType.CREDIT:
      return t(($) => $.common.order.totalPoints.earned);
    case TransactionType.DEBIT:
      return t(($) => $.common.order.totalPoints.redeemed);
    case TransactionType.REDEMPTION_REFUND:
      return t(($) => $.common.order.totalPoints.refunded);
    case TransactionType.RETURN:
      return t(($) => $.common.order.totalPoints.returned);
    case TransactionType.CORRECTION:
    case TransactionType.RETURN_CORRECTION:
      return t(($) => $.common.order.totalPoints.corrected);
    default:
      return t(($) => $.common.order.totalPoints.earned);
  }
}

interface ManualPointsHistoryDetailsModalProps {
  brandColor?: string,
  forceMobileView?: boolean,
  onClose: () => void,
  /** Supplied only by internal views; farmers never see an edit control. */
  onEdit?: () => void,
  pointsHistory: ApiPointsHistory,
}

export const ManualPointsHistoryDetailsModal = ({
  brandColor,
  forceMobileView = false,
  onClose,
  onEdit,
  pointsHistory,
}: ManualPointsHistoryDetailsModalProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { isMobile } = useMediaQuery();
  const displayMobileView = forceMobileView || isMobile;
  const brandColorBg = brandColor ?? theme.palette.primary.main;
  const formattedDate = formatMediumDate(pointsHistory.orderDate as string, i18n);

  const adjustmentTitle = useMemo(
    () => getManualAdjustmentTitle(pointsHistory.type, t),
    [pointsHistory.type, t],
  );
  const isPositive = pointsHistory.amount >= 0;
  const pointsTextColor: Color = isPositive ? 'success' : 'error';
  const { user: customer } = pointsHistory;

  return (
    <DashboardModal
      closeButtonStyle={{
        bgcolor: brandColorBg,
        color: theme.palette.getContrastText(brandColorBg),
        '&:hover': { bgcolor: brandColorBg, opacity: 0.9, borderColor: 'inherit' },
        ...(displayMobileView ? {} : { alignSelf: 'self-end', width: 'fit-content' }),
      }}
      hideCloseButtonIcon
      onClose={onClose}
      open
      testID="manual-points-history-details-modal"
      title={(
        <Text category="title-large">
          {t($ => $.customerDashboard.pointsActivity.adjustment)}
        </Text>
      )}
      titleSx={displayMobileView ? undefined : { alignItems: 'flex-start', px: '20px' }}
    >
      <Stack
        alignItems={displayMobileView ? 'center' : 'flex-start'}
        px={displayMobileView ? '16px' : '20px'}
        width="100%"
      >
        <VSpacer size="3"/>
        <Stack alignItems="center" direction="row" flexWrap="wrap">
          <Text category="body-medium">
            {t($ => $.common.customerName, { name: customer && getAccountName(customer) })}
          </Text>
          <Box
            borderRadius="50%"
            height="4px"
            mx="8px"
            sx={{ bgcolor: theme.palette.text.primary }}
            width="4px"
          />
          <Text category="body-medium">
            {t($ => $.common.labels.erpId, { id: customer?.externalId })}
          </Text>
        </Stack>
        <VSpacer size="4"/>
        <Stack direction="row" gap="16px" width="100%">
          <Stack width="100%">
            <Text
              category="body-small"
              sx={{ color: theme.palette.text.secondary }}
            >
              {t($ => $.common.table.date)}
            </Text>
            <Text>
              {formattedDate}
            </Text>
          </Stack>
        </Stack>
        <VSpacer size="5"/>
        <Divider sx={{ width: '100%' }}/>
        {!!pointsHistory.description && (
          <Stack alignSelf="flex-start" width="100%">
            <VSpacer size="5"/>
            <Text
              category="body-small"
              sx={{ color: theme.palette.text.secondary }}
            >
              {t($ => $.customers.transactions.description)}
            </Text>
            <Text category="body-medium">
              {pointsHistory.description}
            </Text>
          </Stack>
        )}
        <Stack alignSelf="flex-start" width="100%">
          <VSpacer size="5" />
          <Stack direction="row" justifyContent="space-between" width="100%">
            <Text category="title-medium">
              {adjustmentTitle}
            </Text>
            <Stack direction="row" gap="2px">
              <Text category="title-medium" color={pointsTextColor}>
                {isPositive ? '+' : '–'}
              </Text>
              <Text category="title-medium" color={pointsTextColor}>
                {formatNumber(Math.abs(pointsHistory.amount), i18n)}
              </Text>
            </Stack>
          </Stack>
          {!!pointsHistory.subwallet && (
            <Stack direction="row" justifyContent="space-between" ml="8px" py="6px">
              <Text category="body-medium">{pointsHistory.subwallet.name}</Text>
              <Text category="title-small" color={pointsTextColor}>
                {formatNumber(Math.abs(pointsHistory.amount), i18n)}
              </Text>
            </Stack>
          )}
        </Stack>
        {!!onEdit && (
          <>
            <VSpacer size="5" />
            <Button
              onClick={onEdit}
              testID="edit-manual-points-button"
              variant="outlined"
            >
              {t($ => $.customerDashboard.pointsActivity.editManualPoints)}
            </Button>
          </>
        )}
      </Stack>
    </DashboardModal>
  );
};
